import {
  FrameValidationType,
  QuoteFrameProcessingDataProvider
} from '../../../../../webmodule-common/interop/webmodule-interop';
import { FranchiseeQuoteContainerManager } from '../franchisee-quote-manager';
import {
  getV6FrameForContainer,
  getV6QuoteItemForContainer,
  getV6SupplierForContainer,
  isV6,
  quoteItemAssets,
  setProviderData,
  updateQuoteItemContainer
} from '../../../../quotes/data/v6/helper-functions';
import { interpolateExternal } from '../../../../../localization/global';
import { isFrame, isSupplierFrameItem, processQuoteItemState } from '../../../../quotes/data/quote-helper-functions';
import { LangOutOfDateQuoteItemHandler } from '../../../../../localization/scripts/dealer-franchisee/quotes/data/v6/out-of-date-quote-item-handler-lang';
import { Message, MessageType } from '../../../../../webmodule-common/other/messages';
import {
  OutOfDateQuoteItemHandler,
  QuoteItemProcessingCompleteItem
} from '../../../../quotes/views/quote-item-validation-modal';
import { QuoteContainerManager } from '../../../../quotes/data/quote-container';
import { QuoteItemContainer } from '../../../../quotes/data/quote-item-container';
import { showDevelopmentError } from '../../../../../webmodule-common/other/development-error';
import { toJsonStr } from '../../../../../webmodule-common/other/blob-converters';
import { v6Config } from '../../../../v6config/v6config';
import { V6FranchiseeQuoteProviderData } from '../franchisee-quote-provider-data';

export interface ItemContainerCompareResult {
  structureChanged: boolean;
  validationsChanged: boolean;
}

export class V6OutOfDateQuoteItemHandler implements OutOfDateQuoteItemHandler {
  qcm: FranchiseeQuoteContainerManager;

  constructor(qcm: FranchiseeQuoteContainerManager) {
    this.qcm = qcm;
  }

  isOutOfDate(qic: QuoteItemContainer): boolean {
    return this.qcm.isFrameOutOfDate(qic, []);
  }

  isValidatingItem(qic: QuoteItemContainer): boolean {
    return isV6(qic) && isSupplierFrameItem(qic.item);
  }

  displayInfo(qic: QuoteItemContainer): string {
    const si = getV6SupplierForContainer(qic);

    return si?.quoteItemVersion ?? 'N/A';
  }

  async processItemEvent(
    qm: QuoteContainerManager,
    itemContainer: QuoteItemContainer,
    completeItem: QuoteItemProcessingCompleteItem,
    progress: (caption: string) => Promise<void>
  ): Promise<void> {
    //get the v6QuoteItem that was placed in memory that maps to the ui.
    const v6QuoteItem = getV6QuoteItemForContainer(itemContainer);
    if (!v6QuoteItem) {
      //should never happen.
      await showDevelopmentError('quote-completedItem-validation-modal.ts, v6QuoteItem is null');
      return;
    }

    const data = qm.quoteProviderData() as V6FranchiseeQuoteProviderData;
    const input: QuoteFrameProcessingDataProvider = {
      supplierId: qm.quote.supplierId,
      supplierReferenceOverrideKey: qm.quote.quoteOwnerId,
      quantity: itemContainer.item.quantity,
      quoteDefaults: data.defaultOptions,
      quoteIGUs: data.igu,
      quoteItem: v6QuoteItem,
      progress: progress
    };
    const result = await v6Config().processV6QuoteItem(input);

    if (result) {
      if (!result.quoteItem) {
        await showDevelopmentError(
          'V6OutOfDateQuoteItemHandler: processV6QuoteItem returned empty quoteItem; skipping update.'
        );
        await progress(processQuoteItemState.errors);
        return;
      }

      const thumb = isFrame(itemContainer.item) ? (result.thumbnail ?? null) : null;
      const buyInCost = await qm.getBuyInCosts(result.quoteItem['buyIn']);

      const v6QuoteItemStorage = toJsonStr(result.quoteItem) ?? null;

      updateQuoteItemContainer(itemContainer, v6QuoteItemStorage, result.price, buyInCost);
      completeItem.newPrice = itemContainer.price.quantityCost;
      await progress(processQuoteItemState.finalizing);

      const dataVer = itemContainer.data?.recordVersion;
      const newContainer = await qm.saveAndUpdateQuoteItem(
        itemContainer,
        quoteItemAssets({
          data: v6QuoteItemStorage,
          thumbnail: thumb,
          bomView: result.bomView,
          model3d: result.vrml
        })
      );
      itemContainer.item = newContainer.item;
      itemContainer.data = newContainer.data;
      itemContainer.price = newContainer.price;
      itemContainer.buyInData = newContainer.buyInData;
      setProviderData(itemContainer, v6QuoteItemStorage);

      const frameValidationTypeToMessagetType = (type: FrameValidationType): MessageType => {
        switch (type) {
          case FrameValidationType.critical:
            return MessageType.Error;
          case FrameValidationType.warning:
            return MessageType.Warning;
          case FrameValidationType.information:
            return MessageType.Information;
          default:
            return MessageType.Information;
        }
      };
      const validations = getV6FrameForContainer(newContainer)?.validations;
      completeItem.validation =
        validations?.map(x => {
          const m: Message = {
            message: `${interpolateExternal(x.title)} - ${interpolateExternal(x.text)}`,
            messageType: frameValidationTypeToMessagetType(x.type)
          };
          return m;
        }) ?? [];
      if (dataVer === newContainer.data?.recordVersion) {
        completeItem.error = LangOutOfDateQuoteItemHandler.couldNotBeSaved();
        await progress(processQuoteItemState.errors);
      } else {
        completeItem.completionStatus = processQuoteItemState.completed;
        await progress(processQuoteItemState.completed);
      }
    } else await progress(processQuoteItemState.errors);
  }

  get displayInfoTitle(): string {
    return LangOutOfDateQuoteItemHandler.version();
  }

  async finalProcessing(qm: QuoteContainerManager): Promise<boolean> {
    await qm.runAndSaveSSI();
    return true;
  }
}

export interface OutOfDateFailure {
  qic: QuoteItemContainer;
  reason: string;
}

export interface FinalProcessItem {
  qic: QuoteItemContainer;
  completeItem: QuoteItemProcessingCompleteItem;
}
